// Which lifecycle actions the UI offers right now, and the confirmation destructive ones need (FR-30).
// The API decides admission (409 ActionInFlight / ActionRejected); these guards only keep the UI from
// offering what is certain to be refused, and never branch on the phase (AD-8, FR-39).
import type { ActionType, Environment, EnvironmentSummary, InFlight } from '../api/types';

export interface ActionInfo {
  type: ActionType;
  label: string;
  /** Needs the environment name typed to confirm (destructive). */
  destructive: boolean;
  description: string;
}

export const ACTIONS: readonly ActionInfo[] = [
  { type: 'stop', label: 'Stop', destructive: false, description: 'Scales the workloads to zero; data and build stay.' },
  { type: 'start', label: 'Start', destructive: false, description: 'Brings a stopped environment back without replaying any phase.' },
  { type: 'change-build', label: 'Change build', destructive: false, description: 'Replaces the application tree and keeps the database. Traffic stops for the length of the fetch.' },
  { type: 'refresh-data', label: 'Refresh data', destructive: true, description: 'Discards the database and restores a backup again. Your changes to the data are lost.' },
  { type: 'teardown', label: 'Tear down', destructive: true, description: 'Removes workloads, data and host directories. The object stays as the record.' },
];

export function actionInfo(type: ActionType): ActionInfo {
  return ACTIONS.find((a) => a.type === type)!;
}

/** A request this browser has sent and whose effect the API has not reported yet. */
export interface LocalPending {
  type: ActionType | 'delete';
  since: number;
}

export interface Availability {
  /** Per action: undefined = allowed, else the sentence why not. */
  blocked: Partial<Record<ActionType, string>>;
  /** The action holding the environment, for the banner ("refresh-data in flight"). */
  inFlight?: { type: string; id?: string; startedAt?: string; local: boolean };
}

type Env = Pick<EnvironmentSummary, 'name' | 'tornDown' | 'deleting' | 'actionInFlight'> & Partial<Pick<Environment, 'action'>>;

export function availability(env: Env, local?: LocalPending): Availability {
  const blocked: Partial<Record<ActionType, string>> = {};
  const all = (why: string) => ACTIONS.forEach((a) => (blocked[a.type] = why));

  const server: InFlight | undefined = env.action?.inFlight ?? env.actionInFlight;
  let inFlight: Availability['inFlight'];
  if (local) inFlight = { type: local.type, local: true };
  if (server) inFlight = { type: server.type, id: server.id, startedAt: server.startedAt, local: false };

  if (env.tornDown) all(`${env.name} was torn down; no further action is possible.`);
  else if (env.deleting) all(`${env.name} is being deleted.`);
  else if (inFlight) all(`${inFlight.type} is in flight${inFlight.local ? ' (waiting for the API)' : ''}; one action at a time.`);
  return { blocked, inFlight };
}

/** The typed confirmation must repeat the environment name exactly. */
export function confirmationMatches(typed: string, name: string): boolean {
  return typed === name;
}

export function needsConfirmation(type: ActionType | 'delete'): boolean {
  return type === 'delete' || actionInfo(type).destructive;
}

/**
 * Local pending markers expire once the server reports the action (in flight or in history) or after a
 * bound, so a lost response cannot lock the panel forever.
 */
export function localPendingSettled(local: LocalPending, env: Env, actionId: string | undefined, now: number, boundMs = 120_000): boolean {
  if (now - local.since > boundMs) return true;
  if (env.action?.inFlight || env.actionInFlight) return true; // the server's marker takes over
  return !!actionId && !!env.action?.history?.some((h) => h.id === actionId);
}
